import React from 'react';

import { Box, Typography } from '@mui/material';

import { Button } from 'src/components/common';
import { CompanyRelatedBlock } from 'src/components/companyRelatedBlock/CompanyRelatedBlock';
import { verifierManagerFormFlow } from 'src/constants/auth';
import { useAuthSignupVerifier } from 'src/hooks/auth';
import { SxProp } from 'src/types/mui';

import { useCheckIsAdditionalVerifierAdmin } from '../hooks';
import { RelatedVerifier } from '../types';

import { proceedId } from './constants';

export const RelatedCompanyForm = () => {
  const { formRelatedPage, formData, goToNextStep, setFormFlow, setFormData } =
    useAuthSignupVerifier();

  const currentCompany: RelatedVerifier | undefined = formData.currentCompany as
    | RelatedVerifier
    | undefined;

  const [checkIsAdditionalVerifierAdmin, { loading }] = useCheckIsAdditionalVerifierAdmin(
    currentCompany?.id,
  );

  const handleChange = (verifier?: RelatedVerifier) => {
    setFormData({ ...formData, currentCompany: verifier });
  };

  const handleSubmit = React.useCallback(async () => {
    const getVerifierStaffCountResult = await checkIsAdditionalVerifierAdmin();

    const isAdditionalVerifierAdmin =
      getVerifierStaffCountResult.data?.checkIsAdditionalVerifierAdmin.isAdditionalVerifierAdmin;

    if (isAdditionalVerifierAdmin) {
      setFormFlow(verifierManagerFormFlow);
    }
    goToNextStep();
  }, [checkIsAdditionalVerifierAdmin, goToNextStep, setFormFlow]);

  return (
    <React.Fragment>
      <Typography variant="h5">Related company</Typography>

      <Typography variant="body1" color="text.secondary" mt={1}>
        It looks like you may be associated with a company we have already verified. If you are an
        employee of the company below, we can expedite your sign-up with pre-existing data.
        Otherwise, please proceed in your registration as a separate organization.
      </Typography>

      {formRelatedPage.verifiers.map((verifier: RelatedVerifier) => (
        <CompanyRelatedBlock
          verifier={verifier}
          currentCompany={currentCompany}
          handleChange={() => handleChange(verifier)}
          key={verifier.id}
        />
      ))}
      <CompanyRelatedBlock
        isProceed
        currentCompany={currentCompany}
        handleChange={() =>
          handleChange({
            id: proceedId,
          })
        }
      />
      <Box display="flex" justifyContent="center" mt={4}>
        <Button
          type="submit"
          sx={submitButtonStyles}
          size="large"
          variant="contained"
          loading={loading}
          disabled={!currentCompany}
          onClick={handleSubmit}
        >
          Next Step
        </Button>
      </Box>
    </React.Fragment>
  );
};

const submitButtonStyles: SxProp = {
  px: 5,
  display: 'flex',
  alignItems: 'center',
  marginTop: '24px',
  cursor: 'pointer',
};
